import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Tables } from '@/integrations/supabase/types';

export type Article = Tables<'articles'>;

// Article categories for news-style content
export type ArticleCategory = 
  | 'daily-briefing' 
  | 'market-movers' 
  | 'breaking' 
  | 'deep-dive' 
  | 'weekly-outlook'
  | 'analysis'
  | 'market-flash';

interface UseArticlesOptions {
  limit?: number;
  featured?: boolean;
  category?: string;
  articleType?: ArticleCategory;
  excludeIds?: string[];
}

export function useArticles(options?: UseArticlesOptions) {
  return useQuery({
    queryKey: ['articles', options],
    queryFn: async () => {
      let query = supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('publish_date', { ascending: false });
      
      if (options?.featured !== undefined) {
        query = query.eq('featured', options.featured);
      }
      
      if (options?.category) {
        query = query.eq('category', options.category);
      }
      
      if (options?.excludeIds && options.excludeIds.length > 0) {
        query = query.not('id', 'in', `(${options.excludeIds.join(',')})`);
      }
      
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('Error fetching articles:', error);
        throw error;
      }

      return data;
    },
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

// Get the absolute newest article
export function useLatestArticle() {
  return useQuery({
    queryKey: ['latest-article'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .order('publish_date', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching latest article:', error);
        throw error;
      }
      return data;
    },
    refetchInterval: 60 * 1000, // Every minute for freshness
  });
}

// Get most popular article in last 7 days
export function usePopularArticle() {
  return useQuery({
    queryKey: ['popular-article'],
    queryFn: async () => {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .gte('publish_date', weekAgo.toISOString())
        .order('view_count', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching popular article:', error);
        throw error;
      }
      return data;
    },
    refetchInterval: 5 * 60 * 1000,
  });
}

// Get recent articles by category
export function useArticlesByCategory(category: string, limit = 3) {
  return useQuery({
    queryKey: ['articles-by-category', category, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('status', 'published')
        .eq('category', category)
        .order('publish_date', { ascending: false })
        .limit(limit);
      
      if (error) {
        console.error('Error fetching articles by category:', error);
        throw error;
      }
      return data || [];
    },
    refetchInterval: 5 * 60 * 1000,
  });
}

// Helper to get category display info
export function getCategoryInfo(category: string) {
  const categories: Record<string, { label: string; color: string; icon: string }> = {
    'daily-briefing': { label: 'Daily Briefing', color: 'primary', icon: 'newspaper' },
    'market-movers': { label: 'Market Movers', color: 'success', icon: 'trending-up' },
    'breaking': { label: 'Breaking', color: 'destructive', icon: 'flame' },
    'deep-dive': { label: 'Deep Dive', color: 'purple', icon: 'star' },
    'weekly-outlook': { label: 'Weekly Outlook', color: 'warning', icon: 'calendar' },
    'analysis': { label: 'Analysis', color: 'muted', icon: 'file-text' },
    'market-flash': { label: 'Market Flash', color: 'yellow', icon: 'zap' },
  };
  
  return categories[category] || categories['analysis'];
}
